import React from 'react';
import { Mail, CheckCircle, Clock, Archive, Download, MoreVertical, AlertCircle } from 'lucide-react';
import { Email } from './types';

interface MailDetailProps {
    email: Email | null;
    formatDate: (date: string) => string;
    highlightKeywords: (text: string, keywords: string[]) => string;
}

const MailDetail: React.FC<MailDetailProps> = ({
    email,
    formatDate,
    highlightKeywords
}) => {
    if (!email) {
        return (
            <div className="flex-1 bg-white overflow-y-auto h-full flex items-center justify-center">
                <div className="text-center">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Email Seçin</h3>
                    <p className="text-gray-500">
                        Detaylarını görmek için sol taraftan bir email seçin.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-white overflow-y-auto h-full">
            <div className="h-full flex flex-col">
                {/* Email Header */}
                <div className="border-b border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                                <span className={`inline-block w-3 h-3 rounded-full ${email.categoryColor}`}></span>
                                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    {email.category}
                                </span>
                                {email.isMatched && (
                                    <div className="flex items-center text-green-600 text-sm">
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Eşleşti
                                    </div>
                                )}
                            </div>
                            <h1 className="text-xl font-semibold text-gray-900 mb-2">
                                {email.subject}
                            </h1>
                            <div className="flex items-center text-sm text-gray-500 space-x-4">
                                <span>Gönderen: {email.sender}</span>
                                <span className="flex items-center">
                                    <Clock className="h-4 w-4 mr-1" />
                                    {formatDate(email.receivedAt)}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Arşivle">
                                <Archive className="h-5 w-5" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="İndir">
                                <Download className="h-5 w-5" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Daha fazla">
                                <MoreVertical className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Matched Keywords */}
                    {email.matchedKeywords.length > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-medium text-yellow-800 mb-1">
                                        Eşleşen Anahtar Kelimeler
                                    </h4>
                                    <div className="flex flex-wrap gap-1">
                                        {email.matchedKeywords.map((keyword, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-200 text-yellow-800"
                                            >
                                                {keyword}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Email Content */}
                <div className="flex-1 p-6">
                    <div
                        className="prose max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{
                            __html: highlightKeywords(email.content, email.matchedKeywords)
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MailDetail;
